import Head from "next/head"
import Link from "next/link"
import { BlitzPage, Routes } from "@blitzjs/next"
import { useQuery, useMutation } from "@blitzjs/rpc"
import Layout from "app/core/layouts/Layout"
import Suspense from "app/core/components/Suspense"
import getBrandsForDefaultPipes from "app/brands/queries/getBrandsForDefaultPipes"
import deleteDefaultPipe from "app/defaultPipes/mutations/deleteDefaultPipe"
import Container from "@mui/material/Container"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import TableContainer from "@mui/material/TableContainer"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Stack from "@mui/material/Stack"
import Button from "@mui/material/Button"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import Tooltip from "@mui/material/Tooltip"
import getPipelinePublicUrl from "app/pipelines/services/getPipelinePublicUrl"

type BrandForDefaultPipes = Awaited<ReturnType<typeof getBrandsForDefaultPipes>>[number]

type PopulatedDefaultPipesListProps = {
  pipelines: BrandForDefaultPipes["pipelines"]
  brand: BrandForDefaultPipes
  refetch: Function
}

const EmptyDefaultPipesList = () => {
  return (
    <TableRow>
      <TableCell colSpan={4}>
        <Typography>No default pipes</Typography>
      </TableCell>
    </TableRow>
  )
}

const PopulatedDefaultPipesList = ({
  pipelines,
  brand,
  refetch,
}: PopulatedDefaultPipesListProps) => {
  const [deleteDefaultPipeMutation] = useMutation(deleteDefaultPipe)

  const missingWorldwideDomains = brand.domains.filter(
    (d) =>
      !brand.pipelines.some((pipeline) =>
        pipeline.defaultPipes.some(
          (defaultPipe) =>
            defaultPipe.geographies.some((dpg) => dpg.default) &&
            defaultPipe.domains.some((dpd) => dpd.id === d.id)
        )
      )
  )

  return (
    <>
      {pipelines.map((pipeline) =>
        pipeline.defaultPipes.map((defaultPipe) => (
          <TableRow key={defaultPipe.id} hover={true}>
            <TableCell>
              {getPipelinePublicUrl(pipeline)
                .toString()
                .replace(/^.+:\/\//, "")}
            </TableCell>
            <TableCell>
              {defaultPipe.geographies
                .map((g) => g.name)
                .sort()
                .join(", ")}
            </TableCell>
            <TableCell>
              {defaultPipe.domains
                .sort((d1, d2) => d1.name.localeCompare(d2.name))
                .map((d) => {
                  if (defaultPipe.geographies.some((g) => g.default)) {
                    const brandDomain = brand.domains.find((bd) => bd.id === d.id)!
                    const countryNames = brandDomain.everywhereElseCountries.map((c) => c.name)
                    return (
                      <Tooltip
                        key={d.id}
                        title={`${d.name} everywhere else: ${countryNames.join(", ")}`}
                      >
                        <span>{d.name}</span>
                      </Tooltip>
                    )
                  } else {
                    return d.name
                  }
                })
                .reduce((prev: JSX.Element, current: JSX.Element): any => [prev, ", ", current])}
            </TableCell>
            <TableCell sx={{ py: 0 }}>
              <Stack direction="row" justifyContent="flex-end">
                <Link
                  href={Routes.EditDefaultPipePage({
                    defaultPipeId: defaultPipe.id,
                    brandName: brand.name,
                  })}
                  legacyBehavior
                >
                  <IconButton>
                    <EditIcon />
                  </IconButton>
                </Link>
                <IconButton
                  onClick={async () => {
                    if (window.confirm("This will be deleted")) {
                      await deleteDefaultPipeMutation({ id: defaultPipe.id })
                      refetch()
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </TableCell>
          </TableRow>
        ))
      )}
      {missingWorldwideDomains.map((domain) => (
        <TableRow key={domain.id} hover={true}>
          <TableCell sx={{ color: "text.disabled" }}>-</TableCell>
          <TableCell sx={{ color: "text.disabled" }}>WW</TableCell>
          <TableCell sx={{ color: "text.disabled" }}>{domain.name}</TableCell>
          <TableCell></TableCell>
        </TableRow>
      ))}
    </>
  )
}

export const DefaultPipesList = () => {
  const [brands, { refetch }] = useQuery(getBrandsForDefaultPipes, null)

  return (
    <>
      {brands.map((brand) => (
        <Box key={brand.id}>
          <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <Typography sx={{ my: 4, flexGrow: 1 }} variant="h4" component="h2">
              Default Pipes for {brand.name}
            </Typography>
            <Box>
              <Link
                href={{
                  pathname: Routes.NewDefaultPipePage().pathname,
                  query: { brandName: brand.name },
                }}
                legacyBehavior
              >
                <Button variant="contained" startIcon={<AddIcon />}>
                  Create
                </Button>
              </Link>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Pipeline</TableCell>
                  <TableCell>Geography</TableCell>
                  <TableCell>Domains</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {brand.pipelines.length > 0 &&
                brand.pipelines.some((p) => p.defaultPipes.length > 0) ? (
                  <PopulatedDefaultPipesList
                    pipelines={brand.pipelines}
                    brand={brand}
                    refetch={refetch}
                  />
                ) : (
                  <EmptyDefaultPipesList />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </>
  )
}

const DefaultPipesPage: BlitzPage = () => {
  return (
    <>
      <Head>
        <title>Default Pipes</title>
      </Head>

      <Container maxWidth={false} sx={{ pb: 5 }}>
        <Suspense>
          <DefaultPipesList />
        </Suspense>
      </Container>
    </>
  )
}

DefaultPipesPage.authenticate = true
DefaultPipesPage.getLayout = (page) => <Layout>{page}</Layout>

export default DefaultPipesPage
